import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchPlatformById,
  fetchPlatformsByAccountId,
  createPlatform,
  updatePlatform,
  deletePlatform,
  deletePlatformByPlatformAPI,
} from "./platformsAPI";

export const getPlatform = createAsyncThunk(
  "platform/getById",
  async (uuid, thunkAPI) => {
    try {
      return await fetchPlatformById(uuid);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getPlatformsByAccount = createAsyncThunk(
  "platform/getByAccount",
  async (accountId, { rejectWithValue }) => {
    try {
      return await fetchPlatformsByAccountId(accountId);
    } catch (error) {
      return rejectWithValue("Failed to fetch platforms.");
    }
  }
);

export const createPlatformThunk = createAsyncThunk(
  "platform/create",
  async (platform, { rejectWithValue }) => {
    try {
      return await createPlatform(platform);
    } catch (error) {
      return rejectWithValue("Failed to save platform.");
    }
  }
);

export const updatePlatformThunk = createAsyncThunk(
  "platform/update",
  async (updatedPlatform, { rejectWithValue }) => {
    try {
      return await updatePlatform(updatedPlatform.uuid, updatedPlatform);
    } catch (error) {
      return rejectWithValue("Failed to update platform.");
    }
  }
);

export const removePlatform = createAsyncThunk(
  "platform/remove",
  async (uuid, thunkAPI) => {
    try {
      const data = await deletePlatform(uuid);
      return { uuid, data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removePlatformByPlatformApi = createAsyncThunk(
  "platform/removeByPlatformApi",
  async (platformId, thunkAPI) => {
    try {
      const data = await deletePlatformByPlatformAPI(platformId);
      return { platformId, data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const platformsSlice = createSlice({
  name: "platform",
  initialState: {
    platforms: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPlatformsByAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPlatformsByAccount.fulfilled, (state, action) => {
        state.platforms = action.payload;
        state.loading = false;
      })
      .addCase(getPlatformsByAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createPlatformThunk.fulfilled, (state, action) => {
        state.platforms.push(action.payload);
        state.loading = false;
      })

      .addCase(updatePlatformThunk.fulfilled, (state, action) => {
        const index = state.platforms.findIndex(
          (plat) => plat.uuid === action.payload.uuid
        );
        if (index !== -1) {
          state.platforms[index] = action.payload;
        }
        state.loading = false;
      })

      .addCase(removePlatform.fulfilled, (state, action) => {
        state.platforms = state.platforms.filter(
          (platform) => platform.uuid !== action.payload.uuid
        );
        state.loading = false;
      })

      .addCase(removePlatformByPlatformApi.fulfilled, (state, action) => {
        state.platforms = state.platforms.filter(
          (platform) => platform.platformId !== action.payload.platformId
        );
        state.loading = false;
      });
  },
});

export default platformsSlice.reducer;
